import { LayoutDashboard, Menu, Moon, ShoppingCart, Sun, UserCircle, LogOut, HelpCircle, MessageSquare } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react'; // Importamos useState y useEffect

import { useUIStore } from '../estado/useUIStore.ts';
import { useThemeStore } from '../estado/useThemeStore.ts';
import { useAuth } from '../features/auth/useAuth.ts';
import { setAuthToken } from '@/lib/api.ts';
import { api } from '@/lib/api.ts';
import { FeedbackModal } from '../componentes/FeedbackModal.jsx';

export function Navbar({ isFeedbackOpen, setIsFeedbackOpen }) {
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const { isDarkMode, toggleDarkMode } = useThemeStore();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isAuthenticated, user } = useAuth();

  // --- ESTADO PARA EL EFECTO SCROLL ---
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Si bajamos más de 20px, activamos el fondo oscuro
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const isMarketplace = location.pathname === '/marketplace';
  const isCustomerProfile = location.pathname === '/mi-cuenta';

  // --- OBTENER DATOS DE LA TIENDA ---
  const { data: storeData } = useQuery({
    queryKey: ['merchantStore'],
    queryFn: async () => {
      try {
        const { data } = await api.get('/api/merchant/store');
        return data;
      } catch (error) {
        return null;
      }
    },
    enabled: isAuthenticated,
    retry: 1,
  });

  const storefrontUrl = storeData?.slug ? `/t/${storeData.slug}` : '/marketplace';
  
  const getDashboardUrl = () => {
    if (!user) return '/panel';
    const userType = user.user_type?.toLowerCase();
    if (userType === 'customer') return '/mi-cuenta';
    if (userType === 'merchant') return '/panel';
    if (user.is_superuser) return '/super-admin';
    return '/panel';
  };
  
  const getDashboardLabel = () => {
    if (!user) return 'MI PANEL';
    const userType = user.user_type?.toLowerCase();
    if (userType === 'customer') return 'MI CUENTA';
    if (user.is_superuser) return 'SUPER ADMIN';
    return 'MI PANEL';
  };

  const targetDashboardUrl = getDashboardUrl();
  const isAlreadyInDashboard = location.pathname.startsWith(targetDashboardUrl);

  const handleLogout = () => {
    setAuthToken(null, null); // Eliminar ambos tokens
    queryClient.invalidateQueries({ queryKey: ['auth-user'] });
    queryClient.invalidateQueries({ queryKey: ['merchantStore'] });
    queryClient.removeQueries();
    navigate('/inicio-sesion');
  };

  return (
    <header 
      // APLICAMOS LA LÓGICA DEL EFECTO AQUÍ EN EL CLASSNAME
      className={`sticky top-0 z-40 w-full transition-all duration-500 ${
        scrolled 
          ? 'bg-[#050505]/80 backdrop-blur-xl border-b border-white/5 py-0 shadow-lg shadow-black/20' // AL BAJAR
          : 'bg-transparent border-b border-transparent py-2' // AL ESTAR ARRIBA
      }`}
    >
      <div className="flex h-16 items-center justify-between px-4 sm:px-6">
        
        {/* IZQUIERDA */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={toggleSidebar}
            className="p-2 -ml-2 rounded-xl text-white/50 hover:bg-white/5 hover:text-white transition-colors lg:hidden"
            aria-label="Toggle sidebar"
          >
            <Menu className="h-5 w-5" />
          </button>

          <Link to="/" className="flex items-center gap-3 group">
            {/* --- AQUÍ ESTÁ EL CAMBIO DEL LOGO --- */}
            <img 
              src="/logo.png" 
              alt="TiendasYA Logo" 
              fetchPriority="high"
              width="100"
              height="56"
              className="h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>
        </div>
        
        {/* DERECHA */}
        <nav className="flex items-center gap-2 sm:gap-6">
          
          {/* Botón de Feedback - Solo desktop */}
          <button
            onClick={() => setIsFeedbackOpen(true)}
            className="hidden lg:block p-2 text-white/40 hover:text-purple-400 transition-colors"
            aria-label="Enviar feedback"
            title="Enviar feedback"
          >
            <MessageSquare className="h-4 w-4" />
          </button>

          {/* Botón de Ayuda - Solo desktop */}
          <Link
            to="/panel/ayuda"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:block p-2 text-white/40 hover:text-indigo-400 transition-colors"
            aria-label="Centro de ayuda"
            title="Centro de ayuda"
          >
            <HelpCircle className="h-4 w-4" />
          </Link>

          <button
            type="button"
            onClick={toggleDarkMode}
            className="hidden sm:block p-2 text-white/40 hover:text-white transition-colors"
            aria-label="Toggle dark mode"
          >
            {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          
          {/* BOTÓN VER TIENDA */}
          {!isMarketplace && !isCustomerProfile && (
            <Link 
              to={storefrontUrl}
              // LÓGICA: Se oculta en móvil si NO estamos en panel. Si estamos en panel, se muestra.
              className={`${isAlreadyInDashboard ? 'flex' : 'hidden sm:flex'} items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/10 border border-white/10 hover:bg-white/20 transition-all text-white whitespace-nowrap`}
            >
              <ShoppingCart className="h-3.5 w-3.5" />
              <span className="text-[10px] font-bold tracking-widest uppercase">Ver Tienda</span>
            </Link>
          )}
          
          {/* Separador Vertical */}
          {!isAlreadyInDashboard && isAuthenticated && (
             <div className="hidden sm:block h-4 w-px bg-white/10 mx-1"></div>
          )}
          
          {isAuthenticated ? (
            <>
              {/* Botón Panel/Cuenta */}
              {!isAlreadyInDashboard && (
                <Link
                  to={targetDashboardUrl}
                  className="flex items-center gap-2 px-4 py-1.5 sm:px-5 sm:py-2 text-[10px] font-bold tracking-widest uppercase bg-white text-black rounded-full hover:bg-white/90 transition-all shadow-[0_0_15px_rgba(255,255,255,0.1)] hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] whitespace-nowrap"
                >
                  <LayoutDashboard className="h-3 w-3" />
                  <span className="hidden sm:inline">{getDashboardLabel()}</span>
                  <span className="sm:hidden">PANEL</span>
                </Link>
              )}
              
              {/* Botón Logout */}
              <button
                type="button"
                onClick={handleLogout}
                className="p-2 text-white/40 hover:text-red-400 transition-colors"
                title="Cerrar Sesión"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <Link
              to="/inicio-sesion"
              className="flex items-center gap-2 px-5 py-2 text-[10px] font-bold tracking-widest uppercase bg-white text-black rounded-full hover:bg-white/90 transition-all shadow-[0_0_15px_rgba(255,255,255,0.1)] hover:shadow-[0_0_20px_rgba(255,255,255,0.3)]"
            >
              <span>Ingresar</span>
              <UserCircle className="h-3.5 w-3.5" />
            </Link>
          )}
        </nav>
      </div>

      {/* Modal de Feedback */}
      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </header>
  );
}